import { z } from "zod";

export const CiaLevel = z.enum(["niedrig", "mittel", "hoch", "sehr_hoch"]);
export type CiaLevel = z.infer<typeof CiaLevel>;
export const Treatment = z.enum(["Reduction", "Transfer", "Avoid", "Accept"]);
export type Treatment = z.infer<typeof Treatment>;
export const RisikoStatus = z.enum(["Identifiziert", "InBehandlung", "Akzeptiert", "Geschlossen"]);
export type RisikoStatus = z.infer<typeof RisikoStatus>;
export const RiskClass = z.enum(["mäßig", "hoch", "kritisch"]);
export type RiskClass = z.infer<typeof RiskClass>;

export const Risiko = z.object({
  id: z.number().int().positive(),
  code: z.string(),
  identifiedAt: z.number().int(),
  assetOwnerUserId: z.number().int().nullable(),
  primaryAssetId: z.number().int().nullable(),
  primaryAssetText: z.string().nullable(),
  secondaryAssetId: z.number().int().nullable(),
  secondaryAssetText: z.string().nullable(),
  affectedParty: z.string().nullable(),
  ciaConfidentiality: CiaLevel.nullable(),
  ciaIntegrity: CiaLevel.nullable(),
  ciaAvailability: CiaLevel.nullable(),
  ciaAuthenticity: CiaLevel.nullable(),
  bedrohungId: z.number().int().nullable(),
  threatDescription: z.string().nullable(),
  likelihoodBeforeId: z.number().int().nullable(),
  damageBeforeId: z.number().int().nullable(),
  likelihoodReasoning: z.string().nullable(),
  damageReasoning: z.string().nullable(),
  treatment: Treatment.nullable(),
  riskOwnerUserId: z.number().int().nullable(),
  acceptanceReasoning: z.string().nullable(),
  annexAControls: z.string().nullable(),
  likelihoodAfterId: z.number().int().nullable(),
  damageAfterId: z.number().int().nullable(),
  status: RisikoStatus,
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
  // Computed (filled by backend on read):
  riskScoreBefore: z.number().int().nullable().optional(),
  riskScoreAfter: z.number().int().nullable().optional(),
  riskClassBefore: RiskClass.nullable().optional(),
  riskClassAfter: RiskClass.nullable().optional(),
  // Resolved labels (also filled on read for convenience):
  bedrohungName: z.string().nullable().optional(),
  primaryAssetName: z.string().nullable().optional(),
  secondaryAssetName: z.string().nullable().optional(),
  likelihoodBeforeLabel: z.string().nullable().optional(),
  damageBeforeLabel: z.string().nullable().optional(),
  likelihoodAfterLabel: z.string().nullable().optional(),
  damageAfterLabel: z.string().nullable().optional(),
  // Numeric values from FK lookups (for matrix building):
  likelihoodBeforeNumeric: z.number().int().nullable().optional(),
  damageBeforeNumeric: z.number().int().nullable().optional(),
  likelihoodAfterNumeric: z.number().int().nullable().optional(),
  damageAfterNumeric: z.number().int().nullable().optional(),
});
export type Risiko = z.infer<typeof Risiko>;

export const RisikoCreate = z.object({
  code: z.string().min(1).max(80).optional(),
  identifiedAt: z.number().int().positive(),
  assetOwnerUserId: z.number().int().positive().nullable().optional(),
  primaryAssetId: z.number().int().positive().nullable().optional(),
  primaryAssetText: z.string().max(500).nullable().optional(),
  secondaryAssetId: z.number().int().positive().nullable().optional(),
  secondaryAssetText: z.string().max(500).nullable().optional(),
  affectedParty: z.string().max(2000).nullable().optional(),
  ciaConfidentiality: CiaLevel.nullable().optional(),
  ciaIntegrity: CiaLevel.nullable().optional(),
  ciaAvailability: CiaLevel.nullable().optional(),
  ciaAuthenticity: CiaLevel.nullable().optional(),
  bedrohungId: z.number().int().positive().nullable().optional(),
  threatDescription: z.string().max(5000).nullable().optional(),
  likelihoodBeforeId: z.number().int().positive().nullable().optional(),
  damageBeforeId: z.number().int().positive().nullable().optional(),
  likelihoodReasoning: z.string().max(5000).nullable().optional(),
  damageReasoning: z.string().max(5000).nullable().optional(),
  treatment: Treatment.nullable().optional(),
  riskOwnerUserId: z.number().int().positive().nullable().optional(),
  acceptanceReasoning: z.string().max(5000).nullable().optional(),
  annexAControls: z.string().max(2000).nullable().optional(),
  likelihoodAfterId: z.number().int().positive().nullable().optional(),
  damageAfterId: z.number().int().positive().nullable().optional(),
  status: RisikoStatus.optional(),
});
export type RisikoCreate = z.infer<typeof RisikoCreate>;
export const RisikoUpdate = RisikoCreate.partial();
export type RisikoUpdate = z.infer<typeof RisikoUpdate>;

/** Compute the risk class for a given numeric score (likelihood × damage, 1..25). */
export function classifyRisk(score: number | null): "mäßig" | "hoch" | "kritisch" | null {
  if (score == null) return null;
  if (score >= 1 && score <= 3) return "mäßig";
  if (score >= 4 && score <= 12) return "hoch";
  if (score >= 13) return "kritisch"; // covers the 15..25 band; 13/14 don't occur because levels are 1..5
  return null;
}
